import React, { useEffect, useState, useCallback } from "react";
import { api, fmt, fmtDate, todayISO, formatApiError } from "../lib/api";
import { Wallet, Plus, Trash2, ChevronDown, ChevronRight, RefreshCw, TrendingDown } from "lucide-react";

const DEFAULT_CATS = ["Diesel","Salary","Maintenance","Food","Office","Cylinder Repair","Other"];

function PageHeader() {
  return (
    <div className="mb-6">
      <div className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1 flex items-center gap-1.5">
        <Wallet size={11} /> Finance
      </div>
      <h1 className="font-black text-3xl tracking-tight">Expenses</h1>
      <p className="text-sm text-gray-500 mt-1">Cash holders, expense ledger &amp; daily tracking</p>
    </div>
  );
}

function Badge({ children, color = "gray" }) {
  const colors = {
    gray: "bg-gray-100 text-gray-700",
    blue: "bg-blue-50 text-blue-700",
    green: "bg-green-50 text-green-700",
    red: "bg-red-50 text-red-700",
    yellow: "bg-yellow-50 text-yellow-700",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${colors[color] || colors.gray}`}>
      {children}
    </span>
  );
}

export function ExpensesPage() {
  const [holders, setHolders] = useState([]);
  const [cats, setCats]       = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [selectedHolder, setSelectedHolder] = useState(null);
  const [ledger, setLedger]   = useState(null);
  const [loadingLedger, setLoadingLedger] = useState(false);

  // form states
  const [addHolderName, setAddHolderName]   = useState("");
  const [addHolderLoc, setAddHolderLoc]     = useState("");
  const [showHolderForm, setShowHolderForm] = useState(false);
  const [savingHolder, setSavingHolder]     = useState(false);

  const [addCatName, setAddCatName]       = useState("");
  const [showCatForm, setShowCatForm]     = useState(false);
  const [savingCat, setSavingCat]         = useState(false);

  const [expForm, setExpForm] = useState({ date: todayISO(), holder_id: "", category: "", amount: "", description: "" });
  const [showExpForm, setShowExpForm]     = useState(false);
  const [savingExp, setSavingExp]         = useState(false);

  const [xferForm, setXferForm] = useState({ date: todayISO(), holder_id: "", amount: "", direction: "in", description: "" });
  const [showXferForm, setShowXferForm]   = useState(false);
  const [savingXfer, setSavingXfer]       = useState(false);

  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const [h, c, e, t] = await Promise.all([
        api.get("/cash-holders"),
        api.get("/expense-categories"),
        api.get("/expenses"),
        api.get("/cash-transfers"),
      ]);
      setHolders(h.data || []);
      // merge default + custom cats
      const customNames = (c.data || []).map(x => x.name);
      const all = [...DEFAULT_CATS.filter(n => !customNames.includes(n)).map(n => ({ id: n, name: n, _default: true })),
                   ...(c.data || [])];
      setCats(all);
      setExpenses(e.data || []);
      setTransfers(t.data || []);
    } catch (err) {
      setError(formatApiError(err));
    }
  }, []);

  const loadLedger = useCallback(async (hid) => {
    if (!hid) return;
    setLoadingLedger(true);
    try {
      const r = await api.get(`/cash-holders/${hid}/ledger`);
      setLedger(r.data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoadingLedger(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (selectedHolder) loadLedger(selectedHolder.id);
    else setLedger(null);
  }, [selectedHolder, loadLedger]);

  const addHolder = async (e) => {
    e.preventDefault();
    if (!addHolderName.trim()) return;
    setSavingHolder(true);
    try {
      await api.post("/cash-holders", { name: addHolderName.trim(), location: addHolderLoc.trim() });
      setAddHolderName(""); setAddHolderLoc(""); setShowHolderForm(false);
      await load();
    } catch (err) { setError(formatApiError(err)); }
    finally { setSavingHolder(false); }
  };

  const deleteHolder = async (hid) => {
    if (!window.confirm("Delete this holder? All linked expenses will remain.")) return;
    try { await api.delete(`/cash-holders/${hid}`); await load(); if (selectedHolder?.id === hid) setSelectedHolder(null); }
    catch (err) { setError(formatApiError(err)); }
  };

  const addCat = async (e) => {
    e.preventDefault();
    if (!addCatName.trim()) return;
    setSavingCat(true);
    try {
      await api.post("/expense-categories", { name: addCatName.trim() });
      setAddCatName(""); setShowCatForm(false); await load();
    } catch (err) { setError(formatApiError(err)); }
    finally { setSavingCat(false); }
  };

  const deleteCat = async (catId) => {
    try { await api.delete(`/expense-categories/${catId}`); await load(); }
    catch (err) { setError(formatApiError(err)); }
  };

  const addExpense = async (e) => {
    e.preventDefault();
    if (!expForm.holder_id || !expForm.category || !expForm.amount) return;
    setSavingExp(true);
    try {
      await api.post("/expenses", { ...expForm, amount: parseFloat(expForm.amount) });
      setExpForm({ date: todayISO(), holder_id: expForm.holder_id, category: "", amount: "", description: "" });
      setShowExpForm(false);
      await load();
      if (selectedHolder?.id === expForm.holder_id) await loadLedger(expForm.holder_id);
    } catch (err) { setError(formatApiError(err)); }
    finally { setSavingExp(false); }
  };

  const deleteExpense = async (eid) => {
    if (!window.confirm("Delete this expense?")) return;
    try {
      await api.delete(`/expenses/${eid}`);
      await load();
      if (selectedHolder) await loadLedger(selectedHolder.id);
    } catch (err) { setError(formatApiError(err)); }
  };

  const addXfer = async (e) => {
    e.preventDefault();
    if (!xferForm.holder_id || !xferForm.amount) return;
    setSavingXfer(true);
    try {
      await api.post("/cash-transfers", { ...xferForm, amount: parseFloat(xferForm.amount) });
      setXferForm({ date: todayISO(), holder_id: xferForm.holder_id, amount: "", direction: "in", description: "" });
      setShowXferForm(false);
      await load();
      if (selectedHolder?.id === xferForm.holder_id) await loadLedger(xferForm.holder_id);
    } catch (err) { setError(formatApiError(err)); }
    finally { setSavingXfer(false); }
  };

  const deleteXfer = async (tid) => {
    if (!window.confirm("Delete this cash transfer?")) return;
    try {
      await api.delete(`/cash-transfers/${tid}`);
      await load();
      if (selectedHolder) await loadLedger(selectedHolder.id);
    } catch (err) { setError(formatApiError(err)); }
  };

  return (
    <div className="px-6 py-6 max-w-6xl">
      <PageHeader />
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex justify-between">
          <span>{error}</span>
          <button onClick={() => setError("")} className="ml-4 font-bold">×</button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT: holders + categories */}
        <div className="space-y-4">
          {/* Cash Holders */}
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-bold text-gray-800 text-sm uppercase tracking-wider">Cash Holders</h2>
              <button onClick={() => setShowHolderForm(!showHolderForm)}
                className="p-1 hover:bg-gray-100 rounded" data-testid="add-holder-btn">
                <Plus size={15} />
              </button>
            </div>
            {showHolderForm && (
              <form onSubmit={addHolder} className="mb-3 space-y-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <input value={addHolderName} onChange={e => setAddHolderName(e.target.value)} placeholder="Holder name *" required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500" data-testid="holder-name-input" />
                <input value={addHolderLoc} onChange={e => setAddHolderLoc(e.target.value)} placeholder="Location (optional)"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500" />
                <div className="flex gap-2">
                  <button type="submit" disabled={savingHolder}
                    className="flex-1 bg-gray-900 text-white rounded-lg py-1.5 text-sm font-semibold hover:bg-gray-700 disabled:opacity-50" data-testid="save-holder-btn">
                    {savingHolder ? "Saving…" : "Add Holder"}
                  </button>
                  <button type="button" onClick={() => setShowHolderForm(false)} className="px-3 text-sm text-gray-500 hover:text-gray-700">Cancel</button>
                </div>
              </form>
            )}
            {holders.length === 0 ? (
              <div className="text-sm text-gray-400 text-center py-4">No cash holders yet</div>
            ) : (
              <ul className="space-y-1">
                {holders.map(h => (
                  <li key={h.id}
                    className={`flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer transition-colors ${selectedHolder?.id === h.id ? "bg-gray-900 text-white" : "hover:bg-gray-50 text-gray-800"}`}
                    onClick={() => setSelectedHolder(selectedHolder?.id === h.id ? null : h)}
                    data-testid={`holder-item-${h.id}`}>
                    <div>
                      <div className="text-sm font-semibold">{h.name}</div>
                      {h.location && <div className={`text-xs ${selectedHolder?.id === h.id ? "text-gray-300" : "text-gray-400"}`}>{h.location}</div>}
                    </div>
                    <button onClick={ev => { ev.stopPropagation(); deleteHolder(h.id); }}
                      className={`ml-2 ${selectedHolder?.id === h.id ? "text-gray-300 hover:text-red-300" : "text-gray-300 hover:text-red-500"}`}>
                      <Trash2 size={13} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Expense Categories */}
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-bold text-gray-800 text-sm uppercase tracking-wider">Categories</h2>
              <button onClick={() => setShowCatForm(!showCatForm)} className="p-1 hover:bg-gray-100 rounded">
                <Plus size={15} />
              </button>
            </div>
            {showCatForm && (
              <form onSubmit={addCat} className="mb-3 flex gap-2">
                <input value={addCatName} onChange={e => setAddCatName(e.target.value)} placeholder="Category name" required
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-blue-500" data-testid="cat-name-input" />
                <button type="submit" disabled={savingCat} className="bg-gray-900 text-white rounded-lg px-3 py-1.5 text-xs font-semibold disabled:opacity-50" data-testid="save-cat-btn">
                  {savingCat ? "…" : "Add"}
                </button>
              </form>
            )}
            <div className="flex flex-wrap gap-1.5">
              {cats.map(c => (
                <div key={c.id} className="flex items-center gap-1 bg-gray-100 px-2.5 py-1 rounded-full">
                  <span className="text-xs font-medium text-gray-700">{c.name}</span>
                  {!c._default && (
                    <button onClick={() => deleteCat(c.id)} className="text-gray-400 hover:text-red-500 ml-0.5">
                      <Trash2 size={10} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT: entry form + ledger / expense log */}
        <div className="lg:col-span-2 space-y-4">
          {/* Quick entry buttons */}
          <div className="flex gap-3 flex-wrap">
            <button onClick={() => { setShowExpForm(!showExpForm); setShowXferForm(false); }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border transition-colors ${showExpForm ? "bg-red-600 text-white border-red-600" : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"}`}
              data-testid="add-expense-btn">
              <TrendingDown size={14} /> Add Expense
            </button>
            <button onClick={() => { setShowXferForm(!showXferForm); setShowExpForm(false); }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border transition-colors ${showXferForm ? "bg-blue-600 text-white border-blue-600" : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"}`}
              data-testid="cash-transfer-btn">
              <Plus size={14} /> Cash Transfer
            </button>
          </div>

          {/* Expense Form */}
          {showExpForm && (
            <form onSubmit={addExpense} className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
              <h3 className="font-bold text-sm text-gray-800">New Expense</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Date *</label>
                  <input type="date" value={expForm.date} onChange={e => setExpForm(f => ({...f, date: e.target.value}))} required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500" data-testid="expense-date" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Cash Holder *</label>
                  <select value={expForm.holder_id} onChange={e => setExpForm(f => ({...f, holder_id: e.target.value}))} required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500" data-testid="expense-holder">
                    <option value="">Select holder</option>
                    {holders.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Category *</label>
                  <select value={expForm.category} onChange={e => setExpForm(f => ({...f, category: e.target.value}))} required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500" data-testid="expense-category">
                    <option value="">Select category</option>
                    {cats.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Amount (₹) *</label>
                  <input type="number" step="0.01" min="0.01" value={expForm.amount}
                    onChange={e => setExpForm(f => ({...f, amount: e.target.value}))} required placeholder="0.00"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500" data-testid="expense-amount" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Description</label>
                <input value={expForm.description} onChange={e => setExpForm(f => ({...f, description: e.target.value}))} placeholder="Optional notes"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500" />
              </div>
              <div className="flex gap-2">
                <button type="submit" disabled={savingExp}
                  className="bg-gray-900 text-white rounded-lg px-5 py-2 text-sm font-semibold hover:bg-gray-700 disabled:opacity-50" data-testid="submit-expense-btn">
                  {savingExp ? "Saving…" : "Save Expense"}
                </button>
                <button type="button" onClick={() => setShowExpForm(false)} className="text-sm text-gray-500 hover:text-gray-700 px-3">Cancel</button>
              </div>
            </form>
          )}

          {/* Cash Transfer Form */}
          {showXferForm && (
            <form onSubmit={addXfer} className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
              <h3 className="font-bold text-sm text-gray-800">Cash Transfer</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Date *</label>
                  <input type="date" value={xferForm.date} onChange={e => setXferForm(f => ({...f, date: e.target.value}))} required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Cash Holder *</label>
                  <select value={xferForm.holder_id} onChange={e => setXferForm(f => ({...f, holder_id: e.target.value}))} required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500" data-testid="xfer-holder">
                    <option value="">Select holder</option>
                    {holders.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Direction *</label>
                  <select value={xferForm.direction} onChange={e => setXferForm(f => ({...f, direction: e.target.value}))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500" data-testid="xfer-direction">
                    <option value="in">Cash In (from owner)</option>
                    <option value="out">Cash Out (returned to owner)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Amount (₹) *</label>
                  <input type="number" step="0.01" min="0.01" value={xferForm.amount}
                    onChange={e => setXferForm(f => ({...f, amount: e.target.value}))} required placeholder="0.00"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500" data-testid="xfer-amount" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Description</label>
                <input value={xferForm.description} onChange={e => setXferForm(f => ({...f, description: e.target.value}))} placeholder="Optional notes"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500" />
              </div>
              <div className="flex gap-2">
                <button type="submit" disabled={savingXfer}
                  className="bg-blue-600 text-white rounded-lg px-5 py-2 text-sm font-semibold hover:bg-blue-700 disabled:opacity-50" data-testid="submit-xfer-btn">
                  {savingXfer ? "Saving…" : "Record Transfer"}
                </button>
                <button type="button" onClick={() => setShowXferForm(false)} className="text-sm text-gray-500 hover:text-gray-700 px-3">Cancel</button>
              </div>
            </form>
          )}

          {/* Holder Ledger */}
          {selectedHolder && (
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
                <div>
                  <div className="font-bold text-sm text-gray-800">{selectedHolder.name} — Ledger</div>
                  {ledger && (
                    <div className="text-xs text-gray-500 mt-0.5">
                      Balance: <span className={`font-bold ${ledger.closing_balance >= 0 ? "text-green-600" : "text-red-600"}`}>
                        ₹{fmt(Math.abs(ledger.closing_balance))} {ledger.closing_balance < 0 ? "(deficit)" : ""}
                      </span>
                    </div>
                  )}
                </div>
                <button onClick={() => loadLedger(selectedHolder.id)} className="p-1.5 hover:bg-gray-200 rounded-lg" title="Refresh">
                  <RefreshCw size={13} className={loadingLedger ? "animate-spin" : ""} />
                </button>
              </div>
              {loadingLedger ? (
                <div className="p-6 text-center text-sm text-gray-400">Loading ledger…</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="ledger-table">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-100">
                        {["Date","Description","Category","Out (₹)","In (₹)","Balance (₹)",""].map(h => (
                          <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(ledger?.rows || []).map((row, i) => (
                        <tr key={row.id} className={`border-b border-gray-50 ${i % 2 === 0 ? "" : "bg-gray-50/50"}`}>
                          <td className="px-4 py-2.5 whitespace-nowrap text-gray-600">{fmtDate(row.date)}</td>
                          <td className="px-4 py-2.5 text-gray-800 max-w-[200px] truncate">{row.description}</td>
                          <td className="px-4 py-2.5">
                            {row.category && <Badge color="gray">{row.category}</Badge>}
                          </td>
                          <td className="px-4 py-2.5 text-right text-red-600 font-mono">
                            {row.amount < 0 ? fmt(Math.abs(row.amount)) : ""}
                          </td>
                          <td className="px-4 py-2.5 text-right text-green-600 font-mono">
                            {row.amount > 0 ? fmt(row.amount) : ""}
                          </td>
                          <td className={`px-4 py-2.5 text-right font-mono font-semibold ${row.balance < 0 ? "text-red-600" : "text-gray-800"}`}>
                            {fmt(row.balance)}
                          </td>
                          <td className="px-4 py-2.5">
                            <button onClick={() => row.ref_type === "expense" ? deleteExpense(row.id) : deleteXfer(row.id)}
                              className="text-gray-300 hover:text-red-500 transition-colors" title="Delete">
                              <Trash2 size={12} />
                            </button>
                          </td>
                        </tr>
                      ))}
                      {(!ledger?.rows || ledger.rows.length === 0) && (
                        <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-400">No entries yet for this holder</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* All expenses log */}
          {!selectedHolder && (
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                <div className="font-bold text-sm text-gray-800">All Expenses</div>
                <div className="text-xs text-gray-500 mt-0.5">Click a holder on the left to see its ledger</div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="expenses-table">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      {["Date","Holder","Category","Description","Amount (₹)",""].map(h => (
                        <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {expenses.slice(0, 100).map((e, i) => {
                      const holder = holders.find(h => h.id === e.holder_id);
                      return (
                        <tr key={e.id} className={`border-b border-gray-50 ${i % 2 === 0 ? "" : "bg-gray-50/50"}`}>
                          <td className="px-4 py-2.5 whitespace-nowrap text-gray-600">{fmtDate(e.date)}</td>
                          <td className="px-4 py-2.5 font-medium text-gray-800">{holder?.name || "—"}</td>
                          <td className="px-4 py-2.5"><Badge>{e.category}</Badge></td>
                          <td className="px-4 py-2.5 text-gray-500 max-w-[200px] truncate">{e.description}</td>
                          <td className="px-4 py-2.5 text-right font-mono font-semibold text-red-600">₹{fmt(e.amount)}</td>
                          <td className="px-4 py-2.5">
                            <button onClick={() => deleteExpense(e.id)} className="text-gray-300 hover:text-red-500 transition-colors">
                              <Trash2 size={12} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {expenses.length === 0 && (
                      <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-400">No expenses recorded yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ExpensesPage;
